/**
 * AdminRoutes — every /admin/* page. All gated by <AdminRoute>.
 *
 * This is the biggest win from code-splitting — admin pages are NEVER seen
 * by 99%+ of visitors, but used to ship as part of the main bundle. Now
 * the entire admin chunk only downloads when an admin actually navigates
 * to /admin.
 */
import { lazy } from "react";
import { Route } from "react-router-dom";
import AdminRoute from "@/components/AdminRoute";

const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminFrameworks = lazy(() => import("@/pages/admin/AdminFrameworks"));
const AdminAssessments = lazy(() => import("@/pages/admin/AdminAssessments"));
const AdminSocial = lazy(() => import("@/pages/admin/AdminSocial"));
const AdminSocialQueue = lazy(() => import("@/pages/admin/AdminSocialQueue"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminGlossary = lazy(() => import("@/pages/admin/AdminGlossary"));
const AdminTools = lazy(() => import("@/pages/admin/AdminTools"));
const AdminAutomationRefresh = lazy(() => import("@/pages/admin/AdminAutomationRefresh"));
const AdminShareAttribution = lazy(() => import("@/pages/admin/AdminShareAttribution"));
const AdminIndia = lazy(() => import("@/pages/admin/AdminIndia"));
const AdminSuggestions = lazy(() => import("@/pages/admin/AdminSuggestions"));
const AdminMaintenance = lazy(() => import("@/pages/admin/AdminMaintenance"));
const AdminIndiaRiskPulse = lazy(() => import("@/pages/admin/AdminIndiaRiskPulse"));
const AdminWhatsNewEmail = lazy(() => import("@/pages/admin/AdminWhatsNewEmail"));
const AdminEmbedRequests = lazy(() => import("@/pages/admin/AdminEmbedRequests"));
const AdminApiKeys = lazy(() => import("@/pages/admin/AdminApiKeys"));
const AdminFeedback = lazy(() => import("@/pages/admin/AdminFeedback"));
const AdminNewsletters = lazy(() => import("@/pages/admin/AdminNewsletters"));
const AdminForum = lazy(() => import("@/pages/admin/AdminForum"));
const AdminGrcMonitor = lazy(() => import("@/pages/admin/AdminGrcMonitor"));
const AdminSeo = lazy(() => import("@/pages/admin/AdminSeo"));
const AdminToolAnalytics = lazy(() => import("@/pages/admin/AdminToolAnalytics"));
const AdminJobs = lazy(() => import("@/pages/admin/AdminJobs"));
const AdminRegulatorAlerts = lazy(() => import("@/pages/admin/AdminRegulatorAlerts"));
const AdminReport = lazy(() => import("@/pages/admin/AdminReport"));
const AdminReportCampaign = lazy(() => import("@/pages/admin/AdminReportCampaign"));
const AdminApi = lazy(() => import("@/pages/admin/AdminApi"));
const AdminCertifyQuestions = lazy(() => import("@/pages/admin/AdminCertifyQuestions"));
const AdminCredits = lazy(() => import("@/pages/admin/AdminCredits"));
const AdminPro = lazy(() => import("@/pages/admin/AdminPro"));
const AdminReferrals = lazy(() => import("@/pages/admin/AdminReferrals"));
const AdminBadges = lazy(() => import("@/pages/admin/AdminBadges"));
const AdminChatbotKB = lazy(() => import("@/pages/admin/AdminChatbotKB"));
const AdminYoutube = lazy(() => import("@/pages/admin/AdminYoutube"));
const AdminWebinars = lazy(() => import("@/pages/admin/AdminWebinars"));
const AdminWebinarGuests = lazy(() => import("@/pages/admin/AdminWebinarGuests"));
const AdminUploads = lazy(() => import("@/pages/admin/AdminUploads"));
const AdminContentCalendar = lazy(() => import("@/pages/admin/AdminContentCalendar"));
const AdminSpeakers = lazy(() => import("@/pages/admin/AdminSpeakers"));
const AdminMoneyFlow = lazy(() => import("@/pages/admin/AdminMoneyFlow"));
const AdminCodeQuality = lazy(() => import("@/pages/admin/AdminCodeQuality"));

// Small DRY helper — every admin route shares the same <AdminRoute> wrapper.
const A = (path, Component) => (
  <Route key={path} path={path} element={<AdminRoute><Component /></AdminRoute>} />
);

export const adminRoutes = [
  A("/admin", AdminOverview),
  A("/admin/users", AdminUsers),
  A("/admin/frameworks", AdminFrameworks),
  A("/admin/assessments", AdminAssessments),
  A("/admin/social", AdminSocial),
  A("/admin/social/queue", AdminSocialQueue),
  A("/admin/settings", AdminSettings),
  A("/admin/glossary", AdminGlossary),
  A("/admin/tools", AdminTools),
  A("/admin/automations", AdminAutomationRefresh),
  A("/admin/automations/share-attribution", AdminShareAttribution),
  A("/admin/india", AdminIndia),
  A("/admin/suggestions", AdminSuggestions),
  A("/admin/maintenance", AdminMaintenance),
  A("/admin/india-risk-pulse", AdminIndiaRiskPulse),
  A("/admin/india-risk-pulse/api-keys", AdminApiKeys),
  A("/admin/whats-new-email", AdminWhatsNewEmail),
  A("/admin/embed-requests", AdminEmbedRequests),
  A("/admin/feedback", AdminFeedback),
  A("/admin/newsletters", AdminNewsletters),
  A("/admin/forum", AdminForum),
  A("/admin/grc-monitor", AdminGrcMonitor),
  A("/admin/seo", AdminSeo),
  A("/admin/tool-analytics", AdminToolAnalytics),
  A("/admin/pro", AdminPro),
  A("/admin/jobs", AdminJobs),
  A("/admin/regulator-alerts", AdminRegulatorAlerts),
  A("/admin/report", AdminReport),
  A("/admin/report/campaign", AdminReportCampaign),
  A("/admin/api", AdminApi),
  A("/admin/certify/questions", AdminCertifyQuestions),
  A("/admin/credits", AdminCredits),
  A("/admin/referrals", AdminReferrals),
  A("/admin/badges", AdminBadges),
  A("/admin/chatbot", AdminChatbotKB),
  A("/admin/youtube", AdminYoutube),
  A("/admin/webinars", AdminWebinars),
  A("/admin/webinars/guests", AdminWebinarGuests),
  A("/admin/uploads", AdminUploads),
  A("/admin/content-calendar", AdminContentCalendar),
  A("/admin/speakers", AdminSpeakers),
  A("/admin/money-flow", AdminMoneyFlow),
  A("/admin/code-quality", AdminCodeQuality),
];
